// third-party
import { createSlice } from '@reduxjs/toolkit';

// project-imports
import axios from 'utils/axios';
import { dispatch } from '../index';

// types
import { chat } from './chat';
import { CommunityStateProps } from 'types/community';

export const url = process.env.REACT_APP_API_URL + '/api/community/v1';
export const reportUrl = process.env.REACT_APP_API_URL + '/api/report-community/v1';

const initialState: CommunityStateProps = {
  communities: [],
  metaCommunities: {
    pages: 0,
    rows: 0
  },
  selectedCommunity: null,
  totals: {
    totalCommunities: 0,
    storesCount: 0,
    totalMembers: 0,
    netGrowthToday: null,
    netGrowth7d: 0,
    newCommunities7d: 0,
    lastSyncedAt: null,
    actionsNeededCount: 0,
    actionsNeededChannels: [],
    fullNoSuccessorCount: 0
  },
  channelGroups: [],
  dailyGrowth: [],
  error: null
};

export const community = createSlice({
  name: 'community',
  initialState,
  reducers: {
    setCommunities(state, action) {
      state.communities = action.payload;
      state.error = null;
    },
    setMetaCommunities(state, action) {
      state.metaCommunities = action.payload;
    },
    setSelectedCommunity(state, action) {
      state.selectedCommunity = action.payload;
      state.error = null;
    },
    setTotals(state, action) {
      state.totals = action.payload;
      state.error = null;
    },
    setChannelGroups(state, action) {
      state.channelGroups = action.payload;
      state.error = null;
    },
    setDailyGrowth(state, action) {
      state.dailyGrowth = action.payload;
      state.error = null;
    },
    hasCommunityError(state, action) {
      state.error = action.payload;
    }
  }
});

export default community.reducer;

export function getCommunities(page = 1, pageLimit = 20, orderField = 'totalMembers', order = 'desc') {
  return async () => {
    try {
      const response = await axios.get(`${url}/communities`, {
        params: {
          page,
          pageLimit,
          order_field: orderField,
          order
        }
      });

      dispatch(community.actions.setCommunities(response.data.data));
      dispatch(community.actions.setMetaCommunities(response.data.meta));

      return;
    } catch (error: any) {
      dispatch(chat.actions.hasError(error));
      dispatch(community.actions.hasCommunityError(error?.message || 'Não foi possível carregar as comunidades.'));
    }
  };
}

export function getCommunityById(communityId: string) {
  return async () => {
    try {
      const response = await axios.get(`${url}/communities/${communityId}`);
      dispatch(community.actions.setSelectedCommunity(response.data));
      return response.data;
    } catch (error: any) {
      dispatch(chat.actions.hasError(error));
      dispatch(community.actions.hasCommunityError(error?.message || 'Não foi possível carregar a comunidade.'));
    }
  };
}

export function getCommunityTotals() {
  return async () => {
    try {
      const response = await axios.get(`${reportUrl}/totals`);
      dispatch(community.actions.setTotals(response.data));
      return;
    } catch (error: any) {
      dispatch(chat.actions.hasError(error));
      dispatch(
        community.actions.hasCommunityError(error?.message || 'Não foi possível carregar os totais de comunidades.')
      );
    }
  };
}

export function getChannelGroups() {
  return async () => {
    try {
      const response = await axios.get(`${url}/channels`);
      dispatch(community.actions.setChannelGroups(response.data));
      return;
    } catch (error: any) {
      dispatch(chat.actions.hasError(error));
      dispatch(community.actions.hasCommunityError(error?.message || 'Não foi possível carregar o ranking por canal.'));
    }
  };
}

export interface DailyGrowthParams {
  days?: number;
  startDate?: string;
  endDate?: string;
}

export function getDailyGrowth(params: DailyGrowthParams = { days: 7 }) {
  return async () => {
    try {
      const response = await axios.get(`${reportUrl}/daily-growth`, { params });
      dispatch(community.actions.setDailyGrowth(response.data));
      return;
    } catch (error: any) {
      dispatch(chat.actions.hasError(error));
      dispatch(
        community.actions.hasCommunityError(error?.message || 'Não foi possível carregar o crescimento diário.')
      );
    }
  };
}
